import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ref, set, get, update } from 'firebase/database';
import { database } from '../firebase';

const AddContact = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(!!id);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const isEditMode = !!id;

    const [dealerData, setDealerData] = useState({
        dealerName: '',
        mobile1: '',
        email: '',
        dob: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        dealerType: '' // dealer, agent, banker
    });

    useEffect(() => {
        if (isEditMode) {
            loadDealerData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadDealerData = async () => {
        try {
            // Helper function to map prefix to category node
            const getPrefixCategory = (prefix) => {
                const prefixMap = {
                    'DEALER': 'dealers',
                    'AGENT': 'agents',
                    'BANKER': 'bankers',
                    'FINANCE': 'finance_executives',
                    'CUSTOMER': 'customers',
                    'KEY': 'key_persons',
                    'DSA': 'dsa',
                    'BNI': 'bni',
                    'SOCIAL': 'social_media',
                    'OTHER': 'others'
                };
                return prefixMap[prefix] || 'others';
            };

            const prefix = (id || '').toString().split('_')[0];
            const node = getPrefixCategory(prefix);

            const dealerRef = ref(database, `contacts/${node}/${id}`);
            const snapshot = await get(dealerRef);

            if (snapshot.exists()) {
                const data = snapshot.val();
                setDealerData({
                    dealerName: data.dealerName || '',
                    mobile1: data.mobile1 || '',
                    email: data.email || '',
                    dob: data.dob || data.dateOfBirth || '',
                    address: data.address || '',
                    city: data.city || '',
                    state: data.state || '',
                    pincode: data.pincode || '',
                    gstNumber: data.gstNumber || '',
                    panNumber: data.panNumber || '',
                    dealerType: data.dealerType || '',
                    commission: data.commission || '',
                    bankName: data.bankName || '',
                    accountNumber: data.accountNumber || '',
                    ifscCode: data.ifscCode || '',
                    notes: data.notes || ''
                });
            } else {
                setError('Contact not found');
                setTimeout(() => navigate('/contacts'), 2000);
            }
        } catch (error) {
            console.error('Error loading dealer:', error);
            setError('Failed to load dealer data');
        } finally {
            setLoadingData(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        let processedValue = value;

        // Real-time validation
        if (name === 'dealerName') {
            // Only allow letters and spaces
            if (!/^[a-zA-Z\s]*$/.test(value)) return;
        }

        if (name === 'mobile1' || name === 'pincode') {
            // Only allow digits
            processedValue = value.replace(/\D/g, '');
        }

        if (name === 'email') {
            processedValue = value.toLowerCase();
        }

        setDealerData(prev => ({
            ...prev,
            [name]: processedValue
        }));
    };

    const validateMobile = (mobile) => {
        const mobileRegex = /^\d{10}$/;
        return mobileRegex.test(mobile);
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePincode = (pincode) => {
        const pincodeRegex = /^[1-9][0-9]{5}$/;
        return pincodeRegex.test(pincode);
    };

    const generateDealerId = async () => {
        try {
            // Generate a compact random dealer id to avoid sequential predictable IDs
            const rand = Math.random().toString(36).slice(2, 10);
            const time = Date.now().toString(36);
            const type = (dealerData.dealerType || 'dealer').toLowerCase();

            // Map contact type to ID prefix
            const getPrefixForType = (dealerType) => {
                const prefixMap = {
                    'dealer': 'DEALER',
                    'agent': 'AGENT',
                    'banker': 'BANKER',
                    'finance_executive': 'FINANCE',
                    'customer': 'CUSTOMER',
                    'key_person': 'KEY',
                    'dsa': 'DSA',
                    'bni': 'BNI',
                    'social_media': 'SOCIAL',
                    'others': 'OTHER'
                };
                return prefixMap[dealerType] || 'OTHER';
            };

            const prefix = getPrefixForType(type);
            return `${prefix}_${rand}_${time}`;
        } catch (error) {
            console.error('Error generating dealer ID:', error);
            return `OTHER_${Date.now()}`;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields
        if (!dealerData.dealerName.trim()) {
            setError('Contact Name is required');
            return;
        }

        if (!dealerData.mobile1) {
            setError('Mobile Number is required');
            return;
        }

        if (!dealerData.dealerType) {
            setError('Please select a Contact Type');
            return;
        }

        // Validate dealer name (only letters and spaces)
        if (!/^[a-zA-Z\s]+$/.test(dealerData.dealerName.trim())) {
            setError('Contact name should contain only letters and spaces');
            return;
        }

        // Validate mobile 1
        if (!validateMobile(dealerData.mobile1)) {
            setError('Mobile Number 1 must be a valid 10-digit number');
            return;
        }

        // Mobile 2 removed from form

        // Validate email if provided
        if (dealerData.email && !validateEmail(dealerData.email)) {
            setError('Please enter a valid email address');
            return;
        }

        // Validate pincode if provided
        if (dealerData.pincode && !validatePincode(dealerData.pincode)) {
            setError('Pincode must be a valid 6-digit Indian pincode');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            // Check for duplicate mobile and email before adding (only for new contacts)
            if (!isEditMode) {
                // Fetch all contacts from all categories
                const getAllContacts = async () => {
                    const categories = ['dealers', 'agents', 'bankers', 'finance_executives', 'customers', 'key_persons', 'dsa', 'bni', 'social_media', 'others'];
                    const allContacts = [];

                    for (const category of categories) {
                        const contactsRef = ref(database, `contacts/${category}`);
                        const snapshot = await get(contactsRef);

                        if (snapshot.exists()) {
                            const contactsData = snapshot.val();
                            Object.values(contactsData).forEach(contact => {
                                allContacts.push(contact);
                            });
                        }
                    }

                    return allContacts;
                };

                const allContacts = await getAllContacts();

                // Check for duplicate mobile number
                if (dealerData.mobile1) {
                    const mobileExists = allContacts.some(contact =>
                        contact.mobile1 === dealerData.mobile1
                    );

                    if (mobileExists) {
                        setLoading(false);
                        setError(`A contact with mobile number "${dealerData.mobile1}" already exists in the system. Please use a different mobile number.`);
                        return;
                    }
                }

                // Check for duplicate email
                if (dealerData.email) {
                    const emailExists = allContacts.some(contact =>
                        contact.email && contact.email.toLowerCase() === dealerData.email.toLowerCase()
                    );

                    if (emailExists) {
                        setLoading(false);
                        setError(`A contact with email "${dealerData.email}" already exists in the system. Please use a different email address.`);
                        return;
                    }
                }
            }

            if (isEditMode) {
                // Helper function to map prefix to category node
                const getPrefixCategory = (prefix) => {
                    const prefixMap = {
                        'DEALER': 'dealers',
                        'AGENT': 'agents',
                        'BANKER': 'bankers',
                        'FINANCE': 'finance_executives',
                        'CUSTOMER': 'customers',
                        'KEY': 'key_persons',
                        'DSA': 'dsa',
                        'BNI': 'bni',
                        'SOCIAL': 'social_media',
                        'OTHER': 'others'
                    };
                    return prefixMap[prefix] || 'others';
                };

                // Update existing contact: choose node by id prefix so updates go to the right collection
                const editPrefix = (id || '').toString().split('_')[0];
                const editNode = getPrefixCategory(editPrefix);

                const dealerRef = ref(database, `contacts/${editNode}/${id}`);

                const updatedData = {
                    dealerName: dealerData.dealerName,
                    mobile1: dealerData.mobile1,
                    email: dealerData.email || '',
                    dob: dealerData.dob || '',
                    address: dealerData.address || '',
                    city: dealerData.city || '',
                    state: dealerData.state || '',
                    pincode: dealerData.pincode || '',
                    dealerType: dealerData.dealerType,
                    updatedAt: new Date().toISOString()
                };

                await update(dealerRef, updatedData);
                setSuccess('Contact updated successfully!');
            } else {
                // Add new contact: write to node according to selected type
                const dealerId = await generateDealerId();

                // Map contact type to database category
                const getContactCategory = (dealerType) => {
                    const typeMap = {
                        'dealer': 'dealers',
                        'agent': 'agents',
                        'banker': 'bankers',
                        'finance_executive': 'finance_executives',
                        'customer': 'customers',
                        'key_person': 'key_persons',
                        'dsa': 'dsa',
                        'bni': 'bni',
                        'social_media': 'social_media',
                        'others': 'others'
                    };
                    return typeMap[dealerType] || 'others';
                };

                const node = getContactCategory(dealerData.dealerType);
                const dealerRef = ref(database, `contacts/${node}/${dealerId}`);

                const dealer = {
                    dealerId,
                    dealerName: dealerData.dealerName,
                    mobile1: dealerData.mobile1,
                    email: dealerData.email || '',
                    dob: dealerData.dob || '',
                    address: dealerData.address || '',
                    city: dealerData.city || '',
                    state: dealerData.state || '',
                    pincode: dealerData.pincode || '',
                    dealerType: dealerData.dealerType,
                    createdAt: new Date().toISOString(),
                    isActive: true
                };

                await set(dealerRef, dealer);
                setSuccess('Contact added successfully!');
            }
        } catch (error) {
            console.error('Error saving dealer:', error);
            setError(isEditMode ? 'Failed to update dealer. Please try again.' : 'Failed to add dealer. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"></div>
                <p className="text-gray-600 text-lg">Loading dealer data...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-4 sm:p-6 lg:p-8 animate-fadeIn">
            {/* Header */}
            <div className="mb-6 animate-slideUp">
                <button
                    onClick={() => navigate('/contacts')}
                    className="group flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl shadow-md hover:shadow-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 font-medium mb-4 transform hover:scale-105"
                >
                    <span className="group-hover:-translate-x-1 transition-transform duration-200 text-lg">←</span>
                    <span>Back to Contacts</span>
                </button>
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2 animate-gradient">
                    {isEditMode ? 'Edit Contact' : 'Add New Contact'}
                </h1>
                <p className="text-gray-600">
                    {isEditMode ? 'Update contact details and business information' : 'Enter contact details and business information'}
                </p>
            </div>

            {/* Success/Error Messages */}
            {success && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 animate-scaleIn">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                <span className="text-green-600 text-4xl">✓</span>
                            </div>
                            <h3 className="text-2xl font-bold text-green-600 mb-2">Success</h3>
                            <p className="text-gray-600 mb-6">{success}</p>
                            <button
                                onClick={() => {
                                    setSuccess('');
                                    navigate('/contacts');
                                }}
                                className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 animate-scaleIn">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                <span className="text-red-600 text-4xl">✕</span>
                            </div>
                            <h3 className="text-2xl font-bold text-red-600 mb-2">Error</h3>
                            <p className="text-gray-600 mb-6">{error}</p>
                            <button
                                onClick={() => setError('')}
                                className="px-8 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                {/* Basic Information */}
                <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-6 sm:p-8 hover:shadow-2xl transition-all duration-300 animate-slideUp" style={{ animationDelay: '0.1s' }}>
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <span className="text-2xl">📋</span>
                        Basic Information
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div>
                            <label className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="dealerName"
                                value={dealerData.dealerName}
                                onChange={handleChange}
                                placeholder="Enter contact name (letters only)"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all"
                                required
                            />
                        </div>

                        <div>
                            <label className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
                                Mobile Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="tel"
                                name="mobile1"
                                value={dealerData.mobile1}
                                onChange={handleChange}
                                placeholder="10-digit mobile number"
                                maxLength="10"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Date of Birth (DOB)
                            </label>
                            <input
                                type="date"
                                name="dob"
                                value={dealerData.dob}
                                onChange={handleChange}
                                max={new Date().toISOString().split('T')[0]}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all bg-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Email Address
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={dealerData.email}
                                onChange={handleChange}
                                placeholder="Enter email (e.g., dealer@example.com)"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all lowercase"
                            />
                        </div>

                        <div>
                            <label className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-2">
                                Contact Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="dealerType"
                                value={dealerData.dealerType}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all bg-white"
                                required
                            >
                                <option value="" disabled>Select Contact Type</option>
                                <option value="dealer">Dealer</option>
                                <option value="agent">Agent</option>
                                <option value="finance_executive">Finance Executive/Banker</option>
                                <option value="customer">Customer</option>
                                <option value="key_person">Key Person</option>
                                <option value="dsa">DSA</option>
                                <option value="bni">BNI</option>
                                <option value="social_media">Social Media</option>
                                <option value="others">Others</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Address Information */}
                <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-6 sm:p-8 hover:shadow-2xl transition-all duration-300 animate-slideUp" style={{ animationDelay: '0.2s' }}>
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <span className="text-2xl">📍</span>
                        Address Information
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="lg:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Full Address
                            </label>
                            <textarea
                                name="address"
                                value={dealerData.address}
                                onChange={handleChange}
                                placeholder="Enter full address"
                                rows="2"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                City / Village
                            </label>
                            <input
                                type="text"
                                name="city"
                                value={dealerData.city}
                                onChange={handleChange}
                                placeholder="Enter city or village"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                State
                            </label>
                            <select
                                name="state"
                                value={dealerData.state}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all bg-white"
                            >
                                <option value="">Select State</option>
                                <option value="Andhra Pradesh">Andhra Pradesh</option>
                                <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                                <option value="Assam">Assam</option>
                                <option value="Bihar">Bihar</option>
                                <option value="Chhattisgarh">Chhattisgarh</option>
                                <option value="Goa">Goa</option>
                                <option value="Gujarat">Gujarat</option>
                                <option value="Haryana">Haryana</option>
                                <option value="Himachal Pradesh">Himachal Pradesh</option>
                                <option value="Jharkhand">Jharkhand</option>
                                <option value="Karnataka">Karnataka</option>
                                <option value="Kerala">Kerala</option>
                                <option value="Madhya Pradesh">Madhya Pradesh</option>
                                <option value="Maharashtra">Maharashtra</option>
                                <option value="Manipur">Manipur</option>
                                <option value="Meghalaya">Meghalaya</option>
                                <option value="Mizoram">Mizoram</option>
                                <option value="Nagaland">Nagaland</option>
                                <option value="Odisha">Odisha</option>
                                <option value="Punjab">Punjab</option>
                                <option value="Rajasthan">Rajasthan</option>
                                <option value="Sikkim">Sikkim</option>
                                <option value="Tamil Nadu">Tamil Nadu</option>
                                <option value="Telangana">Telangana</option>
                                <option value="Tripura">Tripura</option>
                                <option value="Uttar Pradesh">Uttar Pradesh</option>
                                <option value="Uttarakhand">Uttarakhand</option>
                                <option value="West Bengal">West Bengal</option>
                                <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
                                <option value="Chandigarh">Chandigarh</option>
                                <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
                                <option value="Delhi">Delhi</option>
                                <option value="Jammu and Kashmir">Jammu and Kashmir</option>
                                <option value="Ladakh">Ladakh</option>
                                <option value="Lakshadweep">Lakshadweep</option>
                                <option value="Puducherry">Puducherry</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Pincode
                            </label>
                            <input
                                type="text"
                                name="pincode"
                                value={dealerData.pincode}
                                onChange={handleChange}
                                placeholder="6-digit pincode (e.g., 400001)"
                                maxLength="6"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all"
                            />
                        </div>
                    </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 animate-slideUp" style={{ animationDelay: '0.3s' }}>
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                <span>{isEditMode ? 'Updating Contact...' : 'Adding Contact...'}</span>
                            </>
                        ) : (
                            <>
                                <span>{isEditMode ? '✓ Update Contact' : '✓ Add Contact'}</span>
                            </>
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={() => navigate('/contacts')}
                        className="px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddContact;
